import Icon from '@/components/ui/AppIcon';

interface Stat {
  number: string;
  label: string;
  description: string;
  icon: string;
}

const stats: Stat[] = [
  {
    number: "100+",
    label: "Properties Served",
    description: "Commercial buildings maintained",
    icon: "BuildingOfficeIcon"
  },
  {
    number: "24/7",
    label: "On-Call Availability",
    description: "Quick response guarantee",
    icon: "ClockIcon"
  },
  {
    number: "13+",
    label: "Services Offered",
    description: "Comprehensive solutions",
    icon: "WrenchScrewdriverIcon"
  },
  {
    number: "100%",
    label: "Satisfaction",
    description: "Client-focused approach",
    icon: "StarIcon"
  }
];

const certifications = [
  {
    title: "Fully Licensed",
    description: "All required business licenses and permits",
    icon: "DocumentCheckIcon"
  },
  {
    title: "Insured & Bonded",
    description: "Comprehensive liability coverage",
    icon: "ShieldCheckIcon"
  },
  {
    title: "Certified Engineers",
    description: "4th & 5th class power engineers on staff",
    icon: "AcademicCapIcon"
  },
  {
    title: "Safety First",
    description: "WSBC compliant and safety-trained",
    icon: "CheckBadgeIcon"
  }
];

export default function TrustIndicators() {
  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-surface via-background to-surface relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%231E6FBA' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative">
        {/* Stats Section */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary mb-6">
            <Icon name="ChartBarIcon" size={16} variant="solid" />
            <span>Proven Track Record</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Why Choose OS Property Services
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-12">
            Trusted by property managers across the Lower Mainland for reliable, professional service.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="group">
                <div className="relative p-8 bg-white rounded-2xl border border-border shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  {/* Icon */}
                  <div className="inline-flex p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon name={stat.icon as any} size={32} variant="outline" className="text-primary" />
                  </div>

                  {/* Number */}
                  <div className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    {stat.number}
                  </div>

                  {/* Label */}
                  <div className="font-semibold text-lg text-text-primary mb-1">
                    {stat.label}
                  </div>

                  {/* Description */}
                  <div className="text-sm text-text-secondary">
                    {stat.description}
                  </div>

                  {/* Decorative element */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-accent rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications Section */}
        <div className="bg-white rounded-3xl border border-border shadow-lg p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-text-primary mb-4">
              Licensed, Insured & Certified
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Your peace of mind is our priority. We maintain all necessary certifications and insurance to protect you and your property.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="flex flex-col items-center text-center p-6 rounded-xl bg-surface hover:bg-primary/5 transition-colors duration-300 group">
                <div className="p-4 bg-primary/10 rounded-xl mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <Icon name={cert.icon as any} size={28} variant="outline" className="text-primary group-hover:text-primary-foreground transition-colors duration-300" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">
                  {cert.title}
                </h4>
                <p className="text-sm text-text-secondary">
                  {cert.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
